import { AuthenticatorStorage } from './AuthenticatorStorage'
import { AuthenticatorCrypto } from './AuthenticatorCrypto'
import { AuthenticatorUtils } from './AuthenticatorUtils'
import {
  AuthenticatorEntry,
  AuthenticatorStats,
  AuthenticatorStorage as AuthenticatorStorageData,
  AUTHENTICATOR_DB_VERSION
} from './Types'

class AuthenticatorService {
  private storage: AuthenticatorStorage
  private crypto: AuthenticatorCrypto
  private utils: AuthenticatorUtils

  constructor() {
    this.storage = new AuthenticatorStorage()
    this.crypto = new AuthenticatorCrypto()
    this.utils = new AuthenticatorUtils()
  }

  async initialize(): Promise<void> {
    try {
      await this.storage.initialize()
    } catch (error) {
      throw new Error(
        `Failed to initialize AuthenticatorService: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async addAuthenticator(
    name: string,
    secret: string,
    issuer?: string,
    account?: string
  ): Promise<string> {
    try {
      const id = this.utils.generateId()
      const entry: AuthenticatorEntry = {
        id,
        name: this.utils.sanitizeName(name),
        issuer: this.utils.sanitizeIssuer(issuer),
        account: this.utils.sanitizeAccount(account),
        secret,
        isLocked: false,
        createdAt: Date.now()
      }

      if (!this.utils.validateAuthenticatorData(entry)) {
        throw new Error('Invalid authenticator data')
      }

      await this.storage.addEntry(entry)
      return id
    } catch (error) {
      throw new Error(
        `Failed to add authenticator: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async getAuthenticators(): Promise<AuthenticatorEntry[]> {
    try {
      return await this.storage.getAllEntries()
    } catch (error) {
      throw new Error(
        `Failed to get authenticators: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async getAuthenticator(id: string): Promise<AuthenticatorEntry | null> {
    try {
      return await this.storage.getEntry(id)
    } catch (error) {
      throw new Error(
        `Failed to get authenticator: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async renameAuthenticator(id: string, newName: string): Promise<void> {
    try {
      const authenticator = await this.storage.getEntry(id)
      if (!authenticator) {
        throw new Error('Authenticator not found')
      }

      const updatedAuthenticator: AuthenticatorEntry = {
        ...authenticator,
        name: this.utils.sanitizeName(newName)
      }

      if (!this.utils.validateAuthenticatorData(updatedAuthenticator)) {
        throw new Error('Invalid authenticator name')
      }

      await this.storage.updateEntry(updatedAuthenticator)
    } catch (error) {
      throw new Error(
        `Failed to rename authenticator: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async lockAuthenticator(
    id: string,
    password: string,
    lockId: string
  ): Promise<void> {
    try {
      if (!this.crypto.validatePassword(password)) {
        throw new Error('Invalid password')
      }

      const authenticator = await this.storage.getEntry(id)
      if (!authenticator) {
        throw new Error('Authenticator not found')
      }

      if (authenticator.isLocked) {
        throw new Error('Authenticator is already locked')
      }

      if (!authenticator.secret) {
        throw new Error('Authenticator has no secret to lock')
      }

      const encryptedSecret = await this.crypto.encryptSecret(
        authenticator.secret,
        password
      )

      const updatedAuthenticator: AuthenticatorEntry = {
        ...authenticator,
        secret: undefined,
        encryptedSecret,
        isLocked: true,
        lockId,
        lockedAt: Date.now()
      }

      await this.storage.updateEntry(updatedAuthenticator)
    } catch (error) {
      throw new Error(
        `Failed to lock authenticator: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async unlockAuthenticator(id: string, password: string): Promise<void> {
    try {
      if (!this.crypto.validatePassword(password)) {
        throw new Error('Invalid password')
      }

      const authenticator = await this.storage.getEntry(id)
      if (!authenticator) {
        throw new Error('Authenticator not found')
      }

      if (!authenticator.isLocked) {
        throw new Error('Authenticator is not locked')
      }

      if (!authenticator.encryptedSecret) {
        throw new Error('Authenticator has no encrypted secret to unlock')
      }

      const decryptedSecret = await this.crypto.decryptSecret(
        authenticator.encryptedSecret,
        password
      )

      const updatedAuthenticator: AuthenticatorEntry = {
        ...authenticator,
        secret: decryptedSecret,
        encryptedSecret: undefined,
        isLocked: false,
        lockId: undefined,
        lockedAt: undefined
      }

      await this.storage.updateEntry(updatedAuthenticator)
    } catch (error) {
      throw new Error(
        `Failed to unlock authenticator: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async deleteAuthenticator(id: string): Promise<void> {
    try {
      const authenticator = await this.storage.getEntry(id)
      if (!authenticator) {
        throw new Error('Authenticator not found')
      }

      await this.storage.deleteEntry(id)
    } catch (error) {
      throw new Error(
        `Failed to delete authenticator: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async getStats(): Promise<AuthenticatorStats> {
    try {
      const authenticators = await this.storage.getAllEntries()

      return {
        total: authenticators.length,
        locked: authenticators.filter(a => a.isLocked).length,
        unlocked: authenticators.filter(a => !a.isLocked).length
      }
    } catch (error) {
      throw new Error(
        `Failed to get stats: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async exportData(): Promise<AuthenticatorStorageData> {
    try {
      const authenticators = await this.storage.getAllEntries()

      return {
        version: AUTHENTICATOR_DB_VERSION,
        authenticators,
        lastUpdated: Date.now()
      }
    } catch (error) {
      throw new Error(
        `Failed to export data: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async importData(data: AuthenticatorStorageData): Promise<void> {
    try {
      if (!data.authenticators || !Array.isArray(data.authenticators)) {
        throw new Error('Invalid import data format')
      }

      for (const authenticator of data.authenticators) {
        if (!this.utils.validateAuthenticatorData(authenticator)) {
          throw new Error(
            `Invalid authenticator data for entry: ${
              authenticator.id || 'unknown'
            }`
          )
        }
      }

      await this.storage.importEntries(data.authenticators)
    } catch (error) {
      throw new Error(
        `Failed to import data: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async mergeData(data: AuthenticatorStorageData): Promise<number> {
    try {
      if (!data.authenticators || !Array.isArray(data.authenticators)) {
        throw new Error('Invalid import data format')
      }

      const existingAuthenticators = await this.storage.getAllEntries()
      const existingIds = new Set(existingAuthenticators.map(a => a.id))

      const newAuthenticators = data.authenticators.filter(
        auth => !existingIds.has(auth.id)
      )

      for (const authenticator of newAuthenticators) {
        if (!this.utils.validateAuthenticatorData(authenticator)) {
          throw new Error(
            `Invalid authenticator data for entry: ${
              authenticator.id || 'unknown'
            }`
          )
        }
      }

      let successCount = 0
      for (const authenticator of newAuthenticators) {
        try {
          await this.storage.addEntry(authenticator)
          successCount++
        } catch (error) {
          console.error(
            `Failed to add authenticator ${authenticator.id}:`,
            error
          )
        }
      }

      return successCount
    } catch (error) {
      throw new Error(
        `Failed to merge data: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  async clearAll(): Promise<void> {
    try {
      await this.storage.clearAll()
    } catch (error) {
      throw new Error(
        `Failed to clear all authenticators: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`
      )
    }
  }

  cleanup(): void {
    this.storage.close()
  }
}

export { AuthenticatorService }
